import React, { useMemo } from "react"

import classNames from "classnames"

import { exists } from "shared/lib/exists"
import { Primitive } from "shared/types/Primitive"
import { SelectOption } from "shared/types/Select"
import { InputChangeBehavior } from "shared/ui/input/Input"
import { BoxSelect } from "shared/ui/input/select/box/BoxSelect"
import { TextInput } from "shared/ui/input/text/TextInput"

import { RJSFStringCombobox } from "./RJSFStringCombobox"
import { RJSFFormFieldProps } from "./types"

const MAX_SELECTBOX_OPTIONS = 8

export const RJSFFormStringField = React.memo(
  (
    props: RJSFFormFieldProps & {
      FieldInformationRenderer?: React.ComponentType<RJSFFormFieldProps>
    }
  ) => {
    const { schema, path, isDisabled, FieldInformationRenderer } = props

    const longestValueString = useMemo<number>(() => {
      return (
        schema.options?.reduce(
          (curr, option) => Math.max(option.value.toString().length, curr),
          0
        ) || 0
      )
    }, [schema.options])

    const boxGridClass = useMemo(() => {
      if (longestValueString < 4) {
        return "grid-60"
      }
      if (longestValueString < 6) {
        return "grid-80"
      }
      if (longestValueString < 8) {
        return "grid-100"
      }
      return "grid-120"
    }, [longestValueString])

    if (schema.readOnly) {
      return <RJSFFormReadonlyField {...props} />
    }
    if (
      (schema.options && !schema.enum) ||
      schema.meta?.displayType === "comparator-operator" ||
      (schema.options?.length ?? 0) > MAX_SELECTBOX_OPTIONS
    ) {
      return (
        <div className="rjsf-form__field-container">
          <div className="rjsf-form__field">
            <RJSFStringCombobox
              path={path}
              schema={schema}
              isDisabled={isDisabled}
              creatable={schema.options && !schema.enum}
            />
            {FieldInformationRenderer ? (
              <div className="rjsf-form__field-information">
                <FieldInformationRenderer {...props} />
              </div>
            ) : null}
          </div>
        </div>
      )
    }

    if (schema.options) {
      return (
        <div className="rjsf-form__field-container">
          <div className={classNames("rjsf-form__field", boxGridClass)}>
            <BoxSelect.Root
              name={path}
              label={`${schema.title}${schema.unit ? ` (${schema.unit})` : ""}`}
              className={schema.meta?.displayType}
              disabled={isDisabled}
            >
              <BoxSelect.Label />
              <BoxSelect.Value options={schema.options} />
              <BoxSelect.Input options={schema.options} useValueAsLabel />
              <BoxSelect.ErrorMessage>{schema.error}</BoxSelect.ErrorMessage>
            </BoxSelect.Root>
            {FieldInformationRenderer ? (
              <div className="rjsf-form__field-information">
                <FieldInformationRenderer {...props} />
              </div>
            ) : null}
          </div>
        </div>
      )
    }

    return (
      <div className="rjsf-form__field-container">
        <div className="rjsf-form__field">
          <TextInput.Root
            name={path}
            label={schema.title ?? ""}
            className={schema.meta?.displayType}
            disabled={isDisabled}
          >
            <TextInput.Label />
            <TextInput.Readonly showAlways />
            <TextInput.Focus>
              <TextInput.Input
                changeBehavior={InputChangeBehavior.BLUR}
                type="text"
              />
              <TextInput.Affix>{schema.unit}</TextInput.Affix>
            </TextInput.Focus>
            <TextInput.Readonly />
            <TextInput.ErrorMessage>{schema.error}</TextInput.ErrorMessage>
          </TextInput.Root>
          {FieldInformationRenderer ? (
            <div className="rjsf-form__field-information">
              <FieldInformationRenderer {...props} />
            </div>
          ) : null}
        </div>
      </div>
    )
  }
)

export const RJSFFormReadonlyField = React.memo(
  ({
    options,
    FieldInformationRenderer,
    ...props
  }: RJSFFormFieldProps & {
    options?: SelectOption<Primitive>[]
    FieldInformationRenderer?: React.ComponentType<RJSFFormFieldProps>
  }) => {
    const { schema, path, isDisabled } = props
    const resolvedOptions: SelectOption<Primitive>[] = useMemo(() => {
      if (options) {
        return options
      }
      if (schema.options) {
        return schema.options.map(option =>
          option.value === schema.default
            ? option
            : { ...option, disabled: true }
        )
      }
      if (exists(schema.default)) {
        return [{ label: schema.default?.toString(), value: schema.default }]
      }
      return []
    }, [options, schema.options, schema.default])

    if (resolvedOptions.length > MAX_SELECTBOX_OPTIONS) {
      return (
        <div className="rjsf-form__field-container">
          <div className="rjsf-form__field">
            <RJSFStringCombobox
              path={path}
              schema={schema}
              isDisabled
              creatable={schema.options && !schema.enum}
            />
            {FieldInformationRenderer ? (
              <div className="rjsf-form__field-information">
                <FieldInformationRenderer {...props} />
              </div>
            ) : null}
          </div>
        </div>
      )
    }

    return (
      <div className="rjsf-form__field-container">
        <div className="rjsf-form__field">
          <BoxSelect.Root
            name={path}
            label={`${schema.title}${schema.unit ? ` (${schema.unit})` : ""}`}
            className={schema.meta?.displayType}
            disabled={isDisabled}
          >
            <BoxSelect.Label />
            <BoxSelect.Value options={resolvedOptions} />
            <BoxSelect.Input
              isMulti={false}
              options={resolvedOptions}
              useValueAsLabel
            />
            <BoxSelect.ErrorMessage>{schema.error}</BoxSelect.ErrorMessage>
          </BoxSelect.Root>
          {FieldInformationRenderer ? (
            <div className="rjsf-form__field-information">
              <FieldInformationRenderer {...props} />
            </div>
          ) : null}
        </div>
      </div>
    )
  }
)
